'use client';

import * as m from 'motion/react-m';
import { useEffect, useRef, useState } from 'react';

import { css } from '@flipster/styled-system/css';

const transition = {
  duration: 0.6,
};

interface Props {
  seconds: number;
  size?: number;
  strokeWidth?: number;
  onComplete?: () => unknown;
}

const colorWhite = '#FFFFFF';
const colorBlue = '#3694FF';

export const CountDownSec = ({ size = 50, strokeWidth = 5, seconds, onComplete }: Props) => {
  const timer = useRef<NodeJS.Timeout>(undefined);
  const callbackRef = useRef(onComplete);
  const startTimestampRef = useRef<number>(Date.now());
  callbackRef.current = onComplete;

  const [value, setValue] = useState(seconds);

  const radius = 44;
  const circumference = Math.ceil(2 * Math.PI * radius);
  const percent = 100 - Math.round((100 / seconds) * (value - 0.5));
  const fillPercents = Math.abs(Math.ceil((circumference / 100) * (percent - 100)));

  useEffect(() => {
    startTimestampRef.current = Date.now();

    const tick = () => {
      const currentTimestamp = Date.now();
      const elapsedSeconds = (currentTimestamp - startTimestampRef.current) / 1000;
      const remaining = Math.max(0, seconds - elapsedSeconds);

      setValue(remaining);

      if (remaining > 0) {
        timer.current = setTimeout(tick, 500);
      }
    };

    timer.current = setTimeout(tick, 500);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [seconds]);

  useEffect(() => {
    if (value === 0) callbackRef.current?.();
  }, [value]);

  return (
    <div className={css({ position: 'relative' })} style={{ width: size, height: size }} translate='no'>
      <svg
        viewBox='0 0 100 100'
        version='1.1'
        xmlns='http://www.w3.org/2000/svg'
        width={size}
        height={size}
        className={css({ fill: 'transparent' })}
      >
        <circle cx='50' cy='50' r={radius} stroke={colorWhite} strokeWidth={strokeWidth} strokeLinecap='round' />
      </svg>
      <svg
        viewBox='0 0 100 100'
        width={size}
        height={size}
        className={css({
          position: 'absolute',
          top: 0,
          left: 0,
          transform: 'rotate(-90deg)',
          fill: 'transparent',
        })}
      >
        <m.circle
          cx='50'
          cy='50'
          r={radius}
          stroke={colorBlue}
          strokeWidth={strokeWidth}
          strokeDashoffset={fillPercents}
          strokeDasharray={circumference}
          strokeLinecap='round'
          initial='hidden'
          animate={percent !== 0 ? 'show' : 'hidden'}
          variants={{
            hidden: {
              strokeDashoffset: circumference,
              transition,
            },
            show: {
              strokeDashoffset: fillPercents,
              transition,
            },
          }}
        />
      </svg>
      <p
        className={css({
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 18,
          lineHeight: 1.4,
          color: '#3694FF',
        })}
      >
        {Math.round(value)}
      </p>
    </div>
  );
};
